import React from 'react';
import { Box, TextField } from '@mui/material';

/** F6a: a lot's expiry / manufacture dates (yyyy-MM-dd strings, blank = not given). */
export interface LotDates {
  expiryDate?: string;
  manufactureDate?: string;
}

interface LotDateFieldsProps {
  value: LotDates;
  onChange: (next: LotDates) => void;
  /** The item is expiry-tracked: the expiry date is required. */
  expiryRequired?: boolean;
  disabled?: boolean;
  idPrefix: string;
}

/** Expiry must not be before manufacture (the server refuses it too); returns an error message or null. */
export const lotDatesError = (d: LotDates, expiryRequired?: boolean): string | null => {
  if (expiryRequired && !d.expiryDate) return 'Expiry date is required for this item';
  if (d.expiryDate && d.manufactureDate && d.expiryDate < d.manufactureDate) return 'Expiry date is before the manufacture date';
  return null;
};

/** Only the dates that were entered, for a request body. */
export const lotDatesPayload = (d: LotDates): LotDates => ({
  ...(d.expiryDate ? { expiryDate: d.expiryDate } : {}),
  ...(d.manufactureDate ? { manufactureDate: d.manufactureDate } : {}),
});

const LotDateFields: React.FC<LotDateFieldsProps> = ({ value, onChange, expiryRequired, disabled, idPrefix }) => {
  const error = lotDatesError(value, false);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      <TextField
        size="small" type="date" label={expiryRequired ? 'Expiry date' : 'Expiry date (optional)'}
        data-help="common.lotDates.field.expiry"
        value={value.expiryDate ?? ''} required={!!expiryRequired} disabled={disabled}
        InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 240 }}
        error={!!error} helperText={error ?? undefined}
        inputProps={{ 'data-testid': `${idPrefix}-expiry-date` }}
        onChange={e => onChange({ ...value, expiryDate: e.target.value || undefined })}
      />
      <TextField
        size="small" type="date" label="Manufacture date (optional)"
        data-help="common.lotDates.field.manufacture"
        value={value.manufactureDate ?? ''} disabled={disabled}
        InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 240 }}
        inputProps={{ 'data-testid': `${idPrefix}-manufacture-date` }}
        onChange={e => onChange({ ...value, manufactureDate: e.target.value || undefined })}
      />
    </Box>
  );
};

export default LotDateFields;
